/**
 * TODO: Review Toji's best practices:
 * https://toji.dev/webgpu-best-practices/bind-groups
 * https://toji.dev/webgpu-best-practices/device-loss
 * https://toji.dev/webgpu-best-practices/error-handling
 */

// TODO: Set limits on bin count, small mu_k lets them explode.
// TODO: Touch interactions.
// TODO: Looping vs clamping in step.
// TODO: Step perf.
// TODO: Match up preload tags with elements. Make logo field global preload.

import binFillShader from "./bin-fill.slang";
import binPrefixSumShader from "./bin-prefix-sum.slang";
import particleResetShader from "./particle-reset.slang";
import particleSortShader from "./particle-sort.slang";
import particleStepShader from "./particle-step.slang";
import renderDotShader from "./render-dot.slang";
import renderComposeShader from "./render-compose.slang";
import { resolvePresetParams, type FitMode, type Preset, type ResetSource, type RuntimeParams, type Simulation } from "./types";

const WORKGROUP_SIZE = 64;

const PARAMS_SIZE = 128;
const PARTICLE_SIZE = 32;
const FRAME_SIZE = 16;

const DIRTY_PARAMS = 1 << 0;
const DIRTY_FRAMES = 1 << 1;
const DIRTY_PARTICLE_RESET = 1 << 2;
const DIRTY_BINS = 1 << 3;
const DIRTY_PREFIX_SUM = 1 << 4;
const DIRTY_FIELD = 1 << 5;
const DIRTY_VIEWPORT = 1 << 6;

// Physics runs on a fixed timestep so its speed is the same at any frame rate.
// Calibrated to the original 5 steps per frame at 60 fps.
const STEP_MS = 1000 / 60 / 5;
// Cap catch-up work per frame (covers ~15 fps) and ignore long gaps such as tab switches.
const MAX_STEPS_PER_FRAME = 20;
const MAX_FRAME_GAP_MS = 100;

export function createSimulation({ container, preset }: { container: HTMLElement; preset: Preset }): Simulation {
  const hasFrames = (preset.frames?.length ?? 0) > 0;
  const hasField = !!preset.defaults?.fieldUrl;
  const resetSource: ResetSource = preset.resetSource ?? preset.defaults?.resetSource ?? (hasFrames ? "frames" : hasField ? "field" : "random");

  let frameId: number | null | undefined;
  let dirty = ~0;
  let started = false;
  let stopped = false;
  let paused = false;

  let lastFrameTime: number | null = null;
  let stepAccumulator = 0;

  let prevParams = {} as RuntimeParams;
  let params = {} as RuntimeParams;

  let canvas: HTMLCanvasElement;
  let canvasCSSWidth: number;
  let canvasCSSHeight: number;
  let canvasWidth: number;
  let canvasHeight: number;
  let dpr: number;

  let pointer = new Pointer();

  let device: GPUDevice;
  let context: GPUCanvasContext;
  let surfaceFormat: GPUTextureFormat;

  let dotFormat: GPUTextureFormat = "r16float";
  let dotTexture: GPUTexture | undefined;
  let dotView: GPUTextureView;
  let dotSampler: GPUSampler;

  let fieldTexture: GPUTexture;
  let fieldView: GPUTextureView;
  let fieldSampler: GPUSampler;
  let fieldWidth: number;
  let fieldHeight: number;

  let paramsBuffer: GPUBuffer;
  let frameBuffer: GPUBuffer;
  let frameCount = 0;
  let particleBufferA: GPUBuffer;
  let particleBufferB: GPUBuffer;
  let binBufferA: GPUBuffer;
  let binBufferB: GPUBuffer;
  let binPrefixSumStepSizeBuffer: GPUBuffer;

  let particleResetLayout: GPUBindGroupLayout;
  let particleSortLayout: GPUBindGroupLayout;
  let particleStepLayout: GPUBindGroupLayout;
  let binFillLayout: GPUBindGroupLayout;
  let binPrefixSumLayout: GPUBindGroupLayout;
  let renderDotLayout: GPUBindGroupLayout;
  let renderComposeLayout: GPUBindGroupLayout;

  let particleResetBindGroup: GPUBindGroup;
  let particleSortBindGroup: GPUBindGroup;
  let particleStepBindGroup: GPUBindGroup;
  let binFillBindGroup: GPUBindGroup;
  let binPrefixSumBindGroups: [GPUBindGroup, GPUBindGroup];
  let renderDotBindGroup: GPUBindGroup;
  let renderComposeBindGroup: GPUBindGroup;

  let particleResetFieldPipeline: GPUComputePipeline;
  let particleResetFramesPipeline: GPUComputePipeline;
  let particleResetRandomPipeline: GPUComputePipeline;
  let particleClearPipeline: GPUComputePipeline;
  let particleSortPipeline: GPUComputePipeline;
  let particleStepPipeline: GPUComputePipeline;
  let binClearPipeline: GPUComputePipeline;
  let binFillPipeline: GPUComputePipeline;
  let binPrefixSumPipeline: GPUComputePipeline;
  let renderDotPipeline: GPURenderPipeline;
  let renderComposePipeline: GPURenderPipeline;

  async function start() {
    const adapter = await window.navigator?.gpu?.requestAdapter();
    if (stopped || !adapter) return false;

    device = await adapter.requestDevice();
    if (!device) return false;
    if (stopped) { device.destroy(); return false; }

    canvas = document.createElement("canvas");
    context = canvas.getContext("webgpu") as GPUCanvasContext;
    if (!context) return false;

    surfaceFormat = navigator.gpu.getPreferredCanvasFormat();
    context.configure({ device, format: surfaceFormat, alphaMode: "premultiplied" });

    dotSampler = device.createSampler({ label: "Dot", minFilter: "linear", magFilter: "linear" });

    // TODO: Respect breakpoint and recreate when necessary.
    const fieldBitmap = await fetchBitmap(preset.defaults.fieldUrl);
    if (stopped) return false;

    fieldWidth = fieldBitmap.width;
    fieldHeight = fieldBitmap.height;

    fieldTexture = device.createTexture({
      label: "ResetField",
      size: [fieldWidth, fieldHeight],
      format: "rgba8unorm",
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.RENDER_ATTACHMENT,
    });

    device.queue.copyExternalImageToTexture(
      { source: fieldBitmap },
      { texture: fieldTexture },
      [fieldWidth, fieldHeight],
    );
    fieldBitmap.close();

    fieldView = fieldTexture.createView();
    fieldSampler = device.createSampler({ minFilter: "linear", magFilter: "linear", addressModeU: "clamp-to-edge", addressModeV: "clamp-to-edge" });

    paramsBuffer?.destroy();
    paramsBuffer = device.createBuffer({
      label: "Params",
      size: PARAMS_SIZE,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    let particleResetModule = device.createShaderModule({ label: "ParticleReset", code: particleResetShader });
    let particleSortModule = device.createShaderModule({ label: "ParticleSort", code: particleSortShader });
    let particleStepModule = device.createShaderModule({ label: "ParticleStep", code: particleStepShader });
    let binFillModule = device.createShaderModule({ label: "BinFill", code: binFillShader });
    let binPrefixSumModule = device.createShaderModule({ label: "BinPrefixSum", code: binPrefixSumShader });
    let renderDotModule = device.createShaderModule({ label: "RenderDot", code: renderDotShader });
    let renderComposeModule = device.createShaderModule({ label: "RenderCompose", code: renderComposeShader });

    particleResetLayout = device.createBindGroupLayout({
      label: "ParticleReset",
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
        { binding: 3, visibility: GPUShaderStage.COMPUTE, sampler: {} },
        { binding: 4, visibility: GPUShaderStage.COMPUTE, texture: {} },
      ],
    });

    particleSortLayout = device.createBindGroupLayout({
      label: "ParticleSort",
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },
        { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
        { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },
      ],
    });

    particleStepLayout = device.createBindGroupLayout({
      label: "ParticleStep",
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },
        { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
        { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
      ],
    });

    binFillLayout = device.createBindGroupLayout({
      label: "BinFill",
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },
      ],
    });

    binPrefixSumLayout = device.createBindGroupLayout({
      label: "BinPrefixSum",
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },
        { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform", hasDynamicOffset: true } },
      ],
    });

    renderDotLayout = device.createBindGroupLayout({
      label: "RenderDot",
      entries: [
        { binding: 0, visibility: GPUShaderStage.VERTEX, buffer: { type: "uniform" } },
        { binding: 1, visibility: GPUShaderStage.VERTEX, buffer: { type: "read-only-storage" } },
      ],
    });

    renderComposeLayout = device.createBindGroupLayout({
      label: "RenderCompose",
      entries: [
        { binding: 0, visibility: GPUShaderStage.FRAGMENT, sampler: {} },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
      ],
    });

    const particleResetPipelineLayout = device.createPipelineLayout({
      label: "ParticleReset",
      bindGroupLayouts: [particleResetLayout],
    });

    particleResetFieldPipeline = device.createComputePipeline({
      label: "ParticleResetField",
      layout: particleResetPipelineLayout,
      compute: {
        module: particleResetModule,
        entryPoint: "resetField",
      },
    });

    particleResetFramesPipeline = device.createComputePipeline({
      label: "ParticleResetFrames",
      layout: particleResetPipelineLayout,
      compute: {
        module: particleResetModule,
        entryPoint: "resetFrames",
      },
    });

    particleResetRandomPipeline = device.createComputePipeline({
      label: "ParticleResetRandom",
      layout: particleResetPipelineLayout,
      compute: {
        module: particleResetModule,
        entryPoint: "resetRandom",
      },
    });

    particleClearPipeline = device.createComputePipeline({
      label: "ParticleClear",
      layout: device.createPipelineLayout({
        label: "ParticleClear",
        bindGroupLayouts: [particleSortLayout],
      }),
      compute: {
        module: particleSortModule,
        entryPoint: "clear",
      },
    });

    particleSortPipeline = device.createComputePipeline({
      label: "ParticleSort",
      layout: device.createPipelineLayout({
        label: "ParticleSort",
        bindGroupLayouts: [particleSortLayout],
      }),
      compute: {
        module: particleSortModule,
        entryPoint: "sort",
      },
    });

    particleStepPipeline = device.createComputePipeline({
      label: "ParticleStep",
      layout: device.createPipelineLayout({
        label: "ParticleStep",
        bindGroupLayouts: [particleStepLayout],
      }),
      compute: {
        module: particleStepModule,
        entryPoint: "step",
      },
    });

    binClearPipeline = device.createComputePipeline({
      label: "BinClear",
      layout: device.createPipelineLayout({
        label: "BinClear",
        bindGroupLayouts: [binFillLayout],
      }),
      compute: {
        module: binFillModule,
        entryPoint: "clearBinSize",
      },
    });

    binFillPipeline = device.createComputePipeline({
      label: "BinFill",
      layout: device.createPipelineLayout({
        label: "BinFill",
        bindGroupLayouts: [binFillLayout],
      }),
      compute: {
        module: binFillModule,
        entryPoint: "fillBinSize",
      },
    });

    binPrefixSumPipeline = device.createComputePipeline({
      label: "BinPrefixSum",
      layout: device.createPipelineLayout({
        label: "BinPrefixSum",
        bindGroupLayouts: [binPrefixSumLayout],
      }),
      compute: {
        module: binPrefixSumModule,
        entryPoint: "prefixSum",
      },
    });

    renderDotPipeline = device.createRenderPipeline({
      label: "RenderDot",
      layout: device.createPipelineLayout({
        label: "RenderDot",
        bindGroupLayouts: [renderDotLayout],
      }),
      vertex: {
        module: renderDotModule,
        entryPoint: "vertex",
      },
      fragment: {
        module: renderDotModule,
        entryPoint: "fragment",
        targets: [
          {
            format: dotFormat,
            blend: {
              color: { srcFactor: "one", dstFactor: "one", operation: "add" },
              alpha: { srcFactor: "one", dstFactor: "one", operation: "add" },
            },
          },
        ],
      },
      primitive: { topology: "triangle-strip" },
    });

    renderComposePipeline = device.createRenderPipeline({
      label: "RenderCompose",
      layout: device.createPipelineLayout({
        label: "RenderCompose",
        bindGroupLayouts: [renderComposeLayout],
      }),
      vertex: {
        module: renderComposeModule,
        entryPoint: "vertex",
      },
      fragment: {
        module: renderComposeModule,
        entryPoint: "fragment",
        targets: [
          { format: surfaceFormat },
        ],
      },
      primitive: { topology: "triangle-strip" },
    });

    container.appendChild(canvas);

    pointer.observe(preset.defaults.worldMode === "viewport" ? window : canvas);

    started = true;
    scheduleFrame();

    return true;
  }

  function stop() {
    stopped = true;
    pointer.disconnect();
    cancelAnimationFrame(frameId!);
    context?.unconfigure();
    device?.destroy();
    canvas?.remove();
  }

  function scheduleFrame() {
    if (started && !stopped && !paused && frameId == null) {
      frameId = requestAnimationFrame(frame);
    }
  }

  function frame(now: number) {
    frameId = null;
    if (stopped || paused) return;
    frameId = requestAnimationFrame(frame);

    // FIXED TIMESTEP

    const elapsed = lastFrameTime == null ? 5 * STEP_MS : Math.min(now - lastFrameTime, MAX_FRAME_GAP_MS);
    lastFrameTime = now;
    stepAccumulator += elapsed;
    const steps = Math.min(Math.floor(stepAccumulator / STEP_MS), MAX_STEPS_PER_FRAME);
    stepAccumulator = steps === MAX_STEPS_PER_FRAME ? 0 : stepAccumulator - steps * STEP_MS;

    // CANVAS RESIZING

    const containerRect = container.getBoundingClientRect();

    const nextWidth = Math.ceil(containerRect.width);
    const nextHeight = Math.ceil(containerRect.height);
    const nextDpr = devicePixelRatio;

    if (
      canvasCSSWidth !== nextWidth ||
      canvasCSSHeight !== nextHeight ||
      dpr !== nextDpr
    ) {
      canvasCSSWidth = nextWidth;
      canvasCSSHeight = nextHeight;
      dpr = nextDpr;

      canvasWidth = canvas.width = Math.ceil(dpr * nextWidth);
      canvasHeight = canvas.height = Math.ceil(dpr * nextHeight);

      dotTexture?.destroy();
      dotTexture = device.createTexture({
        label: "Dot",
        size: [canvasWidth, canvasHeight],
        format: dotFormat,
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
      });
      dotView = dotTexture.createView({ label: "Dot" });

      dirty |= DIRTY_VIEWPORT;

      if (hasFrames && resetSource === "frames") {
        dirty |= DIRTY_PARTICLE_RESET;
      }

      Object.assign(params, resolvePresetParams(preset, canvasCSSWidth));
    }

    // COMPUTED PARAMETERS

    if (params.worldMode === "viewport") {
      params.worldWidth = canvasCSSWidth;
      params.worldHeight = canvasCSSHeight;
    }

    if (
      params.mu_k !== prevParams.mu_k ||
      params.sigma_k !== prevParams.sigma_k ||
      params.worldWidth !== prevParams.worldWidth ||
      params.worldHeight !== prevParams.worldHeight
    ) {
      params.w_k = 1 / kernelArea(params.mu_k, params.sigma_k);
      params.binSize = params.mu_k + 3 * params.sigma_k;
      params.gridSizeX = Math.ceil(params.worldWidth / params.binSize);
      params.gridSizeY = Math.ceil(params.worldHeight / params.binSize);
      params.binCount = params.gridSizeX * params.gridSizeY;
      params.prefixSumIterations = Math.ceil(Math.ceil(Math.log2(params.binCount + 1)) / 2) * 2;
    }

    const fieldUvScale = fitScale(params.fieldFit ?? "cover", fieldWidth, fieldHeight, params.worldWidth, params.worldHeight);
    const viewScale = fitScale(params.viewFit ?? "cover", params.worldWidth, params.worldHeight, canvasCSSWidth, canvasCSSHeight);

    params.viewWidth = params.worldWidth * viewScale[0];
    params.viewHeight = params.worldHeight * viewScale[1];

    params.dotSizeX = params.dotSize * 2 / params.viewWidth;
    params.dotSizeY = params.dotSize * 2 / params.viewHeight;

    // RESOURCE (RE-)CREATION

    const nextFrameCount = hasFrames ? preset.frames!.length + 1 : 1;

    if (frameCount !== nextFrameCount) {
      frameCount = nextFrameCount;

      frameBuffer?.destroy();
      frameBuffer = device.createBuffer({
        label: "Frames",
        size: frameCount * FRAME_SIZE,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      });

      dirty |= DIRTY_FRAMES;
    }

    if (hasFrames || (dirty & DIRTY_FRAMES)) {
      const frameData = new Float32Array(frameCount * 4);
      frameData[2] = 1;
      frameData[3] = 1;

      if (hasFrames) {
        const canvasRect = canvas.getBoundingClientRect();

        for (let i = 0; i < preset.frames!.length; i++) {
          const frameOffset = (i + 1) * 4;
          const frame = preset.frames![i]!;
          const el = document.getElementById(frame.id);
          if (!el) {
            frameData[frameOffset + 2] = 1;
            frameData[frameOffset + 3] = 1;
            continue;
          }

          const rect = el.getBoundingClientRect();
          frameData[frameOffset + 0] = rect.left - canvasRect.left - 0.5 * params.viewWidth;
          frameData[frameOffset + 1] = 0.5 * params.viewHeight - (rect.top - canvasRect.top);
          frameData[frameOffset + 2] = rect.width;
          frameData[frameOffset + 3] = -rect.height;
        }
      }

      device.queue.writeBuffer(frameBuffer, 0, frameData);
    }

    if (params.particleCount !== prevParams.particleCount) {
      particleBufferA?.destroy();
      particleBufferA = device.createBuffer({
        label: "ParticlesA",
        size: params.particleCount * PARTICLE_SIZE,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      });

      particleBufferB?.destroy();
      particleBufferB = device.createBuffer({
        label: "ParticlesB",
        size: params.particleCount * PARTICLE_SIZE,
        usage: GPUBufferUsage.STORAGE,
      });

      if (hasFrames) {
        const buffer = new ArrayBuffer(params.particleCount * PARTICLE_SIZE);
        const view = new DataView(buffer);
        let particleIndex = 0;

        for (let frameIndex = 0; frameIndex < preset.frames!.length && particleIndex < params.particleCount; frameIndex++) {
          const frame = preset.frames![frameIndex]!;

          for (const point of frame.particles) {
            if (particleIndex >= params.particleCount) break;

            const offset = particleIndex * PARTICLE_SIZE;

            view.setFloat32(offset + 0, 0, true);
            view.setFloat32(offset + 4, 0, true);
            view.setFloat32(offset + 8, 0, true);
            view.setFloat32(offset + 12, 0, true);
            view.setFloat32(offset + 16, point[0], true);
            view.setFloat32(offset + 20, point[1], true);
            view.setUint32(offset + 24, frameIndex + 1, true);
            view.setUint32(offset + 28, 0, true);

            particleIndex++;
          }
        }

        device.queue.writeBuffer(particleBufferA, 0, buffer);
      }

      dirty |= DIRTY_PARTICLE_RESET;
    }

    if (params.binCount !== prevParams.binCount) {
      binBufferA?.destroy();
      binBufferA = device.createBuffer({
        label: "BinsA",
        size: (params.binCount + 1) * 4,
        usage: GPUBufferUsage.STORAGE,
      });

      binBufferB?.destroy();
      binBufferB = device.createBuffer({
        label: "BinsB",
        size: (params.binCount + 1) * 4,
        usage: GPUBufferUsage.STORAGE,
      });

      dirty |= DIRTY_BINS;
    }

    if (params.prefixSumIterations !== prevParams.prefixSumIterations) {
      const binPrefixSumStepSize = new Uint32Array(params.prefixSumIterations * 64);
      for (let i = 0; i < params.prefixSumIterations; ++i)
        binPrefixSumStepSize[i * 64] = Math.pow(2, i);

      binPrefixSumStepSizeBuffer?.destroy();
      binPrefixSumStepSizeBuffer = device.createBuffer({
        label: "BinPrefixSumStepSize",
        size: params.prefixSumIterations * 256,
        usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.UNIFORM,
      });

      device.queue.writeBuffer(binPrefixSumStepSizeBuffer, 0, binPrefixSumStepSize);

      dirty |= DIRTY_PREFIX_SUM;
    }

    // BIND GROUP (RE-)CREATION

    if (dirty & (DIRTY_PARAMS | DIRTY_PARTICLE_RESET | DIRTY_BINS)) {
      binFillBindGroup = device.createBindGroup({
        label: "BinFill",
        layout: binFillLayout,
        entries: [
          { binding: 0, resource: { buffer: paramsBuffer } },
          { binding: 1, resource: { buffer: particleBufferA } },
          { binding: 2, resource: { buffer: binBufferA } },
        ],
      });
    }

    if (dirty & (DIRTY_PARAMS | DIRTY_BINS | DIRTY_PREFIX_SUM)) {
      binPrefixSumBindGroups = [
        device.createBindGroup({
          label: "BinPrefixSumA",
          layout: binPrefixSumLayout,
          entries: [
            { binding: 0, resource: { buffer: paramsBuffer } },
            { binding: 1, resource: { buffer: binBufferA } },
            { binding: 2, resource: { buffer: binBufferB } },
            { binding: 3, resource: { buffer: binPrefixSumStepSizeBuffer, size: 4 } },
          ],
        }),
        device.createBindGroup({
          label: "BinPrefixSumB",
          layout: binPrefixSumLayout,
          entries: [
            { binding: 0, resource: { buffer: paramsBuffer } },
            { binding: 1, resource: { buffer: binBufferB } },
            { binding: 2, resource: { buffer: binBufferA } },
            { binding: 3, resource: { buffer: binPrefixSumStepSizeBuffer, size: 4 } },
          ],
        }),
      ];
    }

    if (dirty & (DIRTY_PARAMS | DIRTY_FRAMES | DIRTY_PARTICLE_RESET | DIRTY_FIELD)) {
      particleResetBindGroup = device.createBindGroup({
        label: "ParticleReset",
        layout: particleResetLayout,
        entries: [
          { binding: 0, resource: { buffer: paramsBuffer } },
          { binding: 1, resource: { buffer: particleBufferA } },
          { binding: 2, resource: { buffer: frameBuffer } },
          { binding: 3, resource: fieldSampler },
          { binding: 4, resource: fieldView },
        ],
      });
    }

    if (dirty & (DIRTY_PARAMS | DIRTY_PARTICLE_RESET | DIRTY_BINS)) {
      particleSortBindGroup = device.createBindGroup({
        label: "ParticleSort",
        layout: particleSortLayout,
        entries: [
          { binding: 0, resource: { buffer: paramsBuffer } },
          { binding: 1, resource: { buffer: particleBufferA } },
          { binding: 2, resource: { buffer: particleBufferB } },
          { binding: 3, resource: { buffer: binBufferA } },
          { binding: 4, resource: { buffer: binBufferB } },
        ],
      });
    }

    if (dirty & (DIRTY_PARAMS | DIRTY_FRAMES | DIRTY_PARTICLE_RESET | DIRTY_BINS)) {
      particleStepBindGroup = device.createBindGroup({
        label: "ParticleStep",
        layout: particleStepLayout,
        entries: [
          { binding: 0, resource: { buffer: paramsBuffer } },
          { binding: 1, resource: { buffer: particleBufferB } },
          { binding: 2, resource: { buffer: particleBufferA } },
          { binding: 3, resource: { buffer: binBufferA } },
          { binding: 4, resource: { buffer: frameBuffer } },
        ],
      });
    }

    if (dirty & (DIRTY_PARAMS | DIRTY_PARTICLE_RESET)) {
      renderDotBindGroup = device.createBindGroup({
        label: "RenderDot",
        layout: renderDotLayout,
        entries: [
          { binding: 0, resource: { buffer: paramsBuffer } },
          { binding: 1, resource: { buffer: particleBufferA } },
        ],
      });
    }

    if (dirty & DIRTY_VIEWPORT) {
      renderComposeBindGroup = device.createBindGroup({
        label: "RenderCompose",
        layout: renderComposeLayout,
        entries: [
          { binding: 0, resource: dotSampler },
          { binding: 1, resource: dotView },
        ],
      });
    }

    // UPLOAD PARAMS

    device.queue.writeBuffer(paramsBuffer, 0, packParams(params, pointer, fieldUvScale));

    let encoder = device.createCommandEncoder({ label: "MyEncoder" });

    // RESET PARTICLES

    if (dirty & DIRTY_PARTICLE_RESET) {
    const particleResetPipeline = resetSource === "field"
      ? particleResetFieldPipeline
      : resetSource === "frames"
        ? particleResetFramesPipeline
        : particleResetRandomPipeline;

      let resetComputePass = encoder.beginComputePass({ label: "Reset" });
      resetComputePass.setPipeline(particleResetPipeline);
      resetComputePass.setBindGroup(0, particleResetBindGroup);
      resetComputePass.dispatchWorkgroups(Math.ceil(params.particleCount / WORKGROUP_SIZE));
      resetComputePass.end();
    }

    // UPDATE PARTICLES

    let updatePass = encoder.beginComputePass({ label: "Bin" });

    for (let step = 0; step < steps; step++) {
      updatePass.setBindGroup(0, binFillBindGroup);

      updatePass.setPipeline(binClearPipeline);
      updatePass.dispatchWorkgroups(Math.ceil((params.binCount + 1) / WORKGROUP_SIZE));

      updatePass.setPipeline(binFillPipeline);
      updatePass.dispatchWorkgroups(Math.ceil(params.particleCount / WORKGROUP_SIZE));

      updatePass.setPipeline(binPrefixSumPipeline);
      for (let i = 0; i < params.prefixSumIterations; ++i) {
        updatePass.setBindGroup(0, binPrefixSumBindGroups[i % 2]!, [i * 256]);
        updatePass.dispatchWorkgroups(Math.ceil((params.binCount + 1) / WORKGROUP_SIZE));
      }

      updatePass.setBindGroup(0, particleSortBindGroup);

      updatePass.setPipeline(particleClearPipeline);
      updatePass.dispatchWorkgroups(Math.ceil((params.binCount + 1) / WORKGROUP_SIZE));

      updatePass.setPipeline(particleSortPipeline);
      updatePass.dispatchWorkgroups(Math.ceil(params.particleCount / WORKGROUP_SIZE));

      updatePass.setPipeline(particleStepPipeline);
      updatePass.setBindGroup(0, particleStepBindGroup);
      updatePass.dispatchWorkgroups(Math.ceil(params.particleCount / WORKGROUP_SIZE));
    }

    updatePass.end();

    // RENDER PARTICLES

    let dotRenderPass = encoder.beginRenderPass({
      label: "RenderDot",
      colorAttachments: [
        {
          view: dotView,
          clearValue: [0, 0, 0, 0],
          loadOp: "clear",
          storeOp: "store",
        },
      ],
    });
    dotRenderPass.setPipeline(renderDotPipeline);
    dotRenderPass.setBindGroup(0, renderDotBindGroup);
    dotRenderPass.draw(4, params.particleCount);
    dotRenderPass.end();

    // COMPOSE VIEW

    let composeRenderPass = encoder.beginRenderPass({
      label: "RenderCompose",
      colorAttachments: [
        {
          view: context.getCurrentTexture().createView(),
          clearValue: [0, 0, 0, 0],
          loadOp: "clear",
          storeOp: "store",
        },
      ],
    });
    composeRenderPass.setPipeline(renderComposePipeline);
    composeRenderPass.setBindGroup(0, renderComposeBindGroup);
    composeRenderPass.draw(3);
    composeRenderPass.end();

    let commandBuffer = encoder.finish({ label: "MyCommandBuffer" });
    device.queue.submit([commandBuffer]);

    Object.assign(prevParams, params);
    dirty = 0;
  }

  function reset() {
    dirty = ~0;
  }

  function pause() {
    paused = true;
    cancelAnimationFrame(frameId!);
    frameId = null;
  }

  function resume() {
    if (!paused) return;
    paused = false;
    lastFrameTime = null;
    stepAccumulator = 0;
    scheduleFrame();
  }

  return { params, start, stop, reset, pause, resume };
}

function kernelArea(mu: number, sigma: number): number {
  let dr = 0.1 * sigma;
  let R = 3 * sigma;
  let acc = 0;
  let prev: number | null = null;
  for (let r = Math.max(mu - R, 0); r <= mu + R; r += dr) {
    let y = (r - mu) / sigma;
    let v = r * Math.exp(-y * y);
    if (prev != null) acc += (prev + v) * 0.5;
    prev = v;
  }
  return (acc * dr * 2 * Math.PI);
}

function fitScale(
  fit: FitMode,
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number,
): readonly [number, number] {
  const ratio = targetWidth * sourceHeight / (targetHeight * sourceWidth);

  if (fit === "contain") {
    return ratio > 1 ? [ratio, 1] : [1, 1 / ratio];
  }

  return ratio > 1 ? [1, 1 / ratio] : [ratio, 1];
}

async function fetchBitmap(url?: string): Promise<ImageBitmap> {
  if (!url) {
    return createImageBitmap(new ImageData(1, 1));
  }

  let bitmap: ImageBitmap;
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    bitmap = await createImageBitmap(blob, { imageOrientation: "flipY" });
  } catch {
    bitmap = await createImageBitmap(new ImageData(1, 1));
  }
  return bitmap;
}

function packParams(
  params: RuntimeParams,
  pointer: Pointer,
  fieldUvScale: readonly [number, number],
): Float32Array {
  const pointerPosition = pointer.toView(params.viewWidth, params.viewHeight);

  const pointerRadius = pointer.isDown
    ? (params.pointerRadiusDown ?? params.pointerRadius)
    : params.pointerRadius;

  const pointerForce = pointer.isDown
    ? (params.pointerForceDown ?? params.pointerForce)
    : params.pointerForce;

  return new Float32Array([
    params.worldWidth, params.worldHeight,
    params.viewWidth, params.viewHeight,
    params.gridSizeX, params.gridSizeY,
    params.dotSizeX, params.dotSizeY,
    fieldUvScale[0], fieldUvScale[1],
    pointerPosition[0], pointerPosition[1],
    pointerRadius,
    pointerForce,
    params.particleCount,
    params.binCount,
    params.binSize,
    params.returnForce,
    params.returnDamping,
    params.dt,
    params.mu_k,
    params.sigma_k,
    params.w_k,
    params.mu_g,
    params.sigma_g,
    params.c_rep,
    params.r_rep,
    params.resetAttempts,
    params.initialFieldScattering,
  ]);
}

const POINTER_NONE = -99999;

type RectBounds = {
  left: number;
  top: number;
  width: number;
  height: number;
};

class Pointer implements EventListenerObject {
  target: Window | HTMLElement | null = null;
  pointerId: number | null = null;
  isDown = false;
  x = POINTER_NONE;
  y = POINTER_NONE;

  constructor() {
    this.pointerId = null;
    this.isDown = false;
    this.x = POINTER_NONE;
    this.y = POINTER_NONE;
  }

  observe(target: Window | HTMLElement): void {
    if (this.target === target) {
      return;
    }

    if (this.target != null) {
      this.disconnect();
    }

    this.target = target;

    this.target.addEventListener("pointerdown", this);
    this.target.addEventListener("pointerup", this);
    this.target.addEventListener("pointercancel", this);
    this.target.addEventListener("pointermove", this);

    if (this.target === window) {
      this.target.addEventListener("pointerout", this);
      this.target.addEventListener("blur", this);
    } else {
      this.target.addEventListener("pointerenter", this);
      this.target.addEventListener("pointerleave", this);
    }
  }

  disconnect(): void {
    if (this.target) {
      if (this.pointerId != null && this.target !== window && (this.target as HTMLElement).hasPointerCapture(this.pointerId)) {
        (this.target as HTMLElement).releasePointerCapture(this.pointerId);
      }

      this.target.removeEventListener("pointerdown", this);
      this.target.removeEventListener("pointerup", this);
      this.target.removeEventListener("pointercancel", this);
      this.target.removeEventListener("pointermove", this);

      if (this.target === window) {
        this.target.removeEventListener("pointerout", this);
        this.target.removeEventListener("blur", this);
      } else {
        this.target.removeEventListener("pointerenter", this);
        this.target.removeEventListener("pointerleave", this);
      }

      this.target = null;
    }

    this.pointerId = null;
    this.isDown = false;
    this.x = POINTER_NONE;
    this.y = POINTER_NONE;
  }

  handleEvent(e: Event): void {
    if (e.type === "pointerdown") {
      if (this.pointerId != null) return;

      this.pointerId = (e as PointerEvent).pointerId;
      this.isDown = true;
      this.x = (e as PointerEvent).clientX;
      this.y = (e as PointerEvent).clientY;

      if (this.target !== window) {
        (this.target as HTMLElement).setPointerCapture((e as PointerEvent).pointerId);
      }
    } else if (e.type === "pointerup") {
      if ((e as PointerEvent).pointerId !== this.pointerId) return;

      if (this.target !== window && (this.target as HTMLElement).hasPointerCapture((e as PointerEvent).pointerId)) {
        (this.target as HTMLElement).releasePointerCapture((e as PointerEvent).pointerId);
      }

      this.pointerId = null;
      this.isDown = false;

      if (this.isInsideTarget(e as PointerEvent)) {
        this.x = (e as PointerEvent).clientX;
        this.y = (e as PointerEvent).clientY;
      } else {
        this.x = POINTER_NONE;
        this.y = POINTER_NONE;
      }
    } else if (e.type === "pointercancel") {
      if ((e as PointerEvent).pointerId !== this.pointerId) return;

      if (this.target !== window && (this.target as HTMLElement).hasPointerCapture((e as PointerEvent).pointerId)) {
        (this.target as HTMLElement).releasePointerCapture((e as PointerEvent).pointerId);
      }

      this.pointerId = null;
      this.isDown = false;
      this.x = POINTER_NONE;
      this.y = POINTER_NONE;
    } else if (e.type === "pointermove") {
      if (this.pointerId != null && (e as PointerEvent).pointerId !== this.pointerId) return;

      this.x = (e as PointerEvent).clientX;
      this.y = (e as PointerEvent).clientY;
    } else if (e.type === "pointerenter") {
      if (this.pointerId != null && (e as PointerEvent).pointerId !== this.pointerId) return;

      this.x = (e as PointerEvent).clientX;
      this.y = (e as PointerEvent).clientY;
    } else if (e.type === "pointerleave") {
      if (this.pointerId != null) return;

      this.x = POINTER_NONE;
      this.y = POINTER_NONE;
    } else if (e.type === "pointerout") {
      if ((e as PointerEvent).relatedTarget == null) {
        this.pointerId = null;
        this.isDown = false;
        this.x = POINTER_NONE;
        this.y = POINTER_NONE;
      }
    } else if (e.type === "blur") {
      this.pointerId = null;
      this.isDown = false;
      this.x = POINTER_NONE;
      this.y = POINTER_NONE;
    }
  }

  isInsideTarget(e: PointerEvent): boolean {
    const { left, top, width, height } = this.targetBounds();

    return (
      e.clientX >= left &&
      e.clientX <= left + width &&
      e.clientY >= top &&
      e.clientY <= top + height
    );
  }

  targetBounds(): RectBounds {
    if (this.target === window) {
      return { left: 0, top: 0, width: innerWidth, height: innerHeight };
    }

    return (this.target as HTMLElement).getBoundingClientRect();
  }

  toView(viewWidth: number, viewHeight: number): readonly [number, number] {
    const { left, top, width, height } = this.targetBounds();

    return [
      (this.x - left) / width * viewWidth - 0.5 * viewWidth,
      0.5 * viewHeight - (this.y - top) / height * viewHeight,
    ];
  }
}
